/**
 * Payment method badges — signals trust + supported payment options.
 * SVG-only, no external image loads, always renders.
 */

const VISA_SVG = (
  <svg viewBox="0 0 48 16" xmlns="http://www.w3.org/2000/svg" aria-label="Visa" role="img" className="h-full w-auto">
    <rect width="48" height="16" rx="2" fill="#1A1F71" />
    <text x="24" y="12" textAnchor="middle" fontFamily="Arial Black, Arial, sans-serif" fontStyle="italic" fontWeight="900" fontSize="10" fill="#FFFFFF" letterSpacing="0.5">VISA</text>
  </svg>
);

const MASTERCARD_SVG = (
  <svg viewBox="0 0 48 16" xmlns="http://www.w3.org/2000/svg" aria-label="Mastercard" role="img" className="h-full w-auto">
    <rect width="48" height="16" rx="2" fill="#FFFFFF" stroke="#E4E4E7" strokeWidth="0.5" />
    <circle cx="20" cy="8" r="5" fill="#EB001B" />
    <circle cx="28" cy="8" r="5" fill="#F79E1B" fillOpacity="0.9" />
    <path d="M24 4a5 5 0 010 8 5 5 0 010-8z" fill="#FF5F00" />
  </svg>
);

const MPESA_SVG = (
  <svg viewBox="0 0 56 16" xmlns="http://www.w3.org/2000/svg" aria-label="M-PESA" role="img" className="h-full w-auto">
    <rect width="56" height="16" rx="2" fill="#4CB749" />
    <text x="28" y="11" textAnchor="middle" fontFamily="Arial, sans-serif" fontWeight="700" fontSize="8.5" fill="#FFFFFF" letterSpacing="0.3">M-PESA</text>
  </svg>
);

const AMEX_SVG = (
  <svg viewBox="0 0 48 16" xmlns="http://www.w3.org/2000/svg" aria-label="American Express" role="img" className="h-full w-auto">
    <rect width="48" height="16" rx="2" fill="#2E77BB" />
    <text x="24" y="10.5" textAnchor="middle" fontFamily="Arial, sans-serif" fontWeight="900" fontSize="6.5" fill="#FFFFFF" letterSpacing="0.5">AMEX</text>
  </svg>
);

const APPLE_PAY_SVG = (
  <svg viewBox="0 0 48 16" xmlns="http://www.w3.org/2000/svg" aria-label="Apple Pay" role="img" className="h-full w-auto">
    <rect width="48" height="16" rx="2" fill="#000000" />
    <path d="M13.4 6.8c-.6 0-1.4-.4-2.2-.4-1.1 0-2.1.6-2.7 1.6-1.1 2-.3 5 .8 6.6.5.8 1.2 1.6 2 1.6.8 0 1.1-.5 2.1-.5s1.3.5 2.2.5c.9 0 1.5-.8 2-1.6.4-.5.7-1.1.9-1.7-2.4-.9-2.8-4.3-.5-5.5-.7-.9-1.7-.6-2.6-.6zm-.9-1.6c.4-.5.7-1.2.6-1.9-.6.1-1.3.4-1.7.9-.4.4-.8 1.2-.7 1.8.7.1 1.4-.3 1.8-.8z" fill="#FFFFFF" transform="translate(-3 -1.5) scale(0.85)" />
    <text x="30" y="11" fontFamily="Arial, Helvetica, sans-serif" fontWeight="600" fontSize="7" fill="#FFFFFF">Pay</text>
  </svg>
);

const GOOGLE_PAY_SVG = (
  <svg viewBox="0 0 48 16" xmlns="http://www.w3.org/2000/svg" aria-label="Google Pay" role="img" className="h-full w-auto">
    <rect width="48" height="16" rx="2" fill="#FFFFFF" stroke="#DADCE0" strokeWidth="0.5" />
    <text x="16" y="10.5" textAnchor="middle" fontFamily="Arial, sans-serif" fontWeight="500" fontSize="6.5" letterSpacing="0.1">
      <tspan fill="#4285F4">G</tspan>
      <tspan fill="#DB4437">o</tspan>
      <tspan fill="#F4B400">o</tspan>
      <tspan fill="#4285F4">g</tspan>
      <tspan fill="#0F9D58">l</tspan>
      <tspan fill="#DB4437">e</tspan>
    </text>
    <text x="35" y="10.5" textAnchor="middle" fontFamily="Arial, sans-serif" fontWeight="600" fontSize="6.5" fill="#3C4043">Pay</text>
  </svg>
);

const BADGES = {
  visa: { svg: VISA_SVG, label: "Visa" },
  mastercard: { svg: MASTERCARD_SVG, label: "Mastercard" },
  mpesa: { svg: MPESA_SVG, label: "M-PESA" },
  amex: { svg: AMEX_SVG, label: "American Express" },
  applepay: { svg: APPLE_PAY_SVG, label: "Apple Pay" },
  googlepay: { svg: GOOGLE_PAY_SVG, label: "Google Pay" },
};

/**
 * Renders a horizontal row of payment method badges.
 * @param methods - array of keys (visa, mastercard, mpesa, amex, applepay, googlepay)
 * @param align - "left" | "center"
 * @param size - "sm" | "md" (height 20px vs 28px)
 */
export default function PaymentBadges({
  methods = ["visa", "mastercard", "mpesa", "applepay", "googlepay"],
  align = "left",
  size = "sm",
  label = "Accepted payments",
}) {
  const h = size === "md" ? "h-7" : "h-5";
  const wrap = align === "center" ? "justify-center" : "justify-start";
  return (
    <div data-testid="payment-badges" className={`flex flex-col ${align === "center" ? "items-center" : "items-start"} gap-2`}>
      {label ? (
        <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
      ) : null}
      <div className={`flex flex-wrap ${wrap} items-center gap-2`}>
        {methods.map((m) => {
          const b = BADGES[m];
          if (!b) return null;
          return (
            <span
              key={m}
              data-testid={`payment-badge-${m}`}
              title={b.label}
              className={`${h} inline-flex items-center`}
              aria-label={b.label}
            >
              {b.svg}
            </span>
          );
        })}
      </div>
    </div>
  );
}
